/**
 * CallLogsTab — searchable, filterable, sortable, paginated Grandstream call
 * log for an event. Shown only on grandstream campuses.
 *
 * Data comes from sp_getGrandstreamCallLogs (via fetchEventCallLogs). Each
 * row includes the raw core_event_calls columns plus these derived fields
 * the table renders:
 *   - call_type_name : human label for the call type
 *   - caller         : display name of the caller
 *   - receiver       : area / user / resident name depending on callee type
 *   - create_time    : UTC timestamp of the call (converted to campus-local)
 *
 * Mirrors the user-profile LogsTab pattern (search + per-column-type filter
 * dropdown + sort + pagination) using the shared dp-* / pp-* classes.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import Pagination from '../../../../components/v2/Pagination.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

// Receiver profile route by callee_type_id (matches the Call Logs report):
//   1 → area (uses the proc's area_id alias)
//   2 → user (callee_id is the user_id)
//   3 → resident (callee_id is the resident_id)
const buildReceiverLink = (row) => {
    const type = parseInt(row.callee_type_id, 10);
    if (type === 1 && row.area_id) return `/profiles/area/${row.area_id}`;
    if (type === 2 && row.callee_id) return `/profiles/user/${row.callee_id}`;
    if (type === 3 && row.callee_id) return `/profiles/resident/${row.callee_id}`;
    return null;
};

const CallLogsTab = ({ callLogs, tzOffset }) => {
    const [search, setSearch] = useState('');
    const [callTypeFilter, setCallTypeFilter] = useState('');
    const [sortCol, setSortCol] = useState('call_start_time');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // Distinct call types for the filter dropdown
    const callTypes = useMemo(() => {
        const types = new Set();
        (callLogs || []).forEach((row) => {
            if (row.call_type_name) types.add(row.call_type_name);
        });
        return [...types].sort();
    }, [callLogs]);

    const filtered = useMemo(() => {
        let rows = [...(callLogs || [])];

        if (search) {
            const q = search.toLowerCase();
            rows = rows.filter((r) =>
                (r.call_type_name || '').toLowerCase().includes(q) ||
                (r.caller || '').toLowerCase().includes(q) ||
                (r.receiver || '').toLowerCase().includes(q) ||
                (r.call_length || '').toString().toLowerCase().includes(q) ||
                (r.create_time || '').toLowerCase().includes(q)
            );
        }

        if (callTypeFilter) {
            rows = rows.filter((r) => r.call_type_name === callTypeFilter);
        }

        rows.sort((a, b) => {
            const aVal = (a[sortCol] || '').toString().toLowerCase();
            const bVal = (b[sortCol] || '').toString().toLowerCase();
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return rows;
    }, [callLogs, search, callTypeFilter, sortCol, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

    const handleSort = useCallback((col) => {
        if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    }, [sortCol]);

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
        return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">Call Logs</h3>
                <div className="pp-section__actions">
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                        {filtered.length} {filtered.length === 1 ? 'call' : 'calls'}
                    </span>
                </div>
            </div>
            <div className="pp-section__body">
                <div className="dp-controls">
                    <div className="dp-controls__left">
                        <div className="dp-search">
                            <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                                className="dp-search__input"
                                type="text"
                                placeholder="Search calls…"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                        {callTypes.length > 0 ? (
                            <select
                                className="dp-select"
                                value={callTypeFilter}
                                onChange={(e) => { setCallTypeFilter(e.target.value); setPage(1); }}
                                style={{ maxWidth: '200px' }}
                            >
                                <option value="">All Call Types</option>
                                {callTypes.map((ct) => (<option key={ct} value={ct}>{ct}</option>))}
                            </select>
                        ) : null}
                    </div>
                </div>

                {(callLogs || []).length === 0 ? (
                    <div className="dp-empty" style={{ marginTop: 'var(--space-4)' }}>
                        <p className="dp-empty__title">No call logs</p>
                        <p className="dp-empty__text">No Grandstream calls were recorded for this event.</p>
                    </div>
                ) : (
                    <>
                        <div className="dp-table-wrap" style={{ marginTop: 'var(--space-3)' }}>
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        <th className="dp-th" aria-sort={ariaSort(sortCol, sortDir, 'call_start_time')}><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('call_start_time')}>Time <SortIcon col="call_start_time" /></button></div></th>
                                        <th className="dp-th" aria-sort={ariaSort(sortCol, sortDir, 'call_length')}><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('call_length')}>Duration <SortIcon col="call_length" /></button></div></th>
                                        <th className="dp-th" aria-sort={ariaSort(sortCol, sortDir, 'call_type_name')}><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('call_type_name')}>Call Type <SortIcon col="call_type_name" /></button></div></th>
                                        <th className="dp-th" aria-sort={ariaSort(sortCol, sortDir, 'caller')}><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('caller')}>Caller <SortIcon col="caller" /></button></div></th>
                                        <th className="dp-th" aria-sort={ariaSort(sortCol, sortDir, 'receiver')}><div className="dp-th__content"><button className="dp-th__sort-btn" onClick={() => handleSort('receiver')}>Receiver <SortIcon col="receiver" /></button></div></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((row, idx) => {
                                        // Caller is resolved via core_user in the proc, so a caller
                                        // name + caller_id means a linkable user profile.
                                        const callerLinkable = Boolean(row.caller_id) && Boolean(row.caller);
                                        const receiverLink = buildReceiverLink(row);
                                        return (
                                            <tr key={row.event_call_id || row.call_id || idx} className="dp-tr">
                                                <td className="dp-td"><span className="dp-cell-text dp-cell-text--muted" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{convertIsoUTCToLocal(row.call_start_time, tzOffset) || '—'}</span></td>
                                                <td className="dp-td"><span className="dp-cell-text" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{row.call_length || '—'}</span></td>
                                                <td className="dp-td"><span className="dp-cell-text">{row.call_type_name || '—'}</span></td>
                                                <td className="dp-td">
                                                    {callerLinkable ? (
                                                        <Link to={`/profiles/user/${row.caller_id}`} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.caller}</Link>
                                                    ) : (
                                                        <span className="dp-cell-text">{row.caller || '—'}</span>
                                                    )}
                                                </td>
                                                <td className="dp-td">
                                                    {row.receiver && receiverLink ? (
                                                        <Link to={receiverLink} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.receiver}</Link>
                                                    ) : (
                                                        <span className="dp-cell-text">{row.receiver || '—'}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {filtered.length > 0 ? (
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                total={filtered.length}
                                perPage={perPage}
                                onPageChange={setPage}
                                onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                                entityLabel={filtered.length === 1 ? 'call' : 'calls'}
                                selectId="event-call-logs-per-page"
                            />
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};

export default CallLogsTab;
